/**
 * Conversão de data em fuso local (GMT-3), num lugar só.
 *
 * ## Por que isto existe
 *
 * O backend grava data em coluna `timestamp without time zone` e serializa sem
 * offset — `"2026-08-17T14:30:00"`, sem `Z`. Quem recebe essa string tem que
 * decidir como lê-la, e é aí que o sistema errava em dois sentidos opostos.
 *
 * ## A distinção que evita regressão
 *
 * **String da API se RECORTA. `Date` se CONVERTE.**
 *
 * - `apiString.substring(0, 10)` é seguro: cirurgia de texto, nenhum fuso
 *   envolvido. Existem 27 sites assim no projeto e **todos estão corretos** —
 *   não "conserte" nenhum deles.
 * - `new Date(...).toISOString()` converte local → UTC. Em GMT-3 (UTC−3),
 *   qualquer horário local a partir das 21:00 **vira o dia seguinte**. É o bug
 *   que estas funções substituem.
 *
 * Detalhe que explica por que o bug parecia intermitente: sobre um `Date` em
 * meia-noite local, `toISOString()` cai em 03:00 UTC do mesmo dia e o
 * truncamento acerta. Só erra quando o `Date` carrega hora de relógio real.
 */

/** Regex de ISO-8601 parcial: data pura, com hora, com ou sem fuso. */
const ISO = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * `Date` → `yyyy-MM-dd` usando os componentes **locais**.
 *
 * Substitui `toISOString().substring(0, 10)`, que converte para UTC antes de
 * recortar e por isso adianta o dia à noite.
 */
export function dataLocalIso(data: Date | null | undefined): string {
  if (!data || isNaN(data.getTime())) return '';

  const ano = data.getFullYear();
  const mes = `${data.getMonth() + 1}`.padStart(2, '0');
  const dia = `${data.getDate()}`.padStart(2, '0');

  return `${ano}-${mes}-${dia}`;
}

/**
 * Texto da API → `Date`, sem nunca tratar data pura como UTC.
 *
 * `new Date('2026-08-17')` nativo interpreta data pura como **meia-noite UTC**,
 * que em GMT-3 cai no dia 16 às 21:00 — daí sair um dia a menos em ordenação,
 * cálculo de atraso e soma de meses. Aqui a data pura é montada como meia-noite
 * local.
 *
 * String que traz fuso explícito (`Z` ou `±hh:mm`) é respeitada: ali o remetente
 * já disse qual é o instante, e reinterpretar seria errado.
 */
export function parseDataApi(valor: string | Date | null | undefined): Date | null {
  if (!valor) return null;

  if (valor instanceof Date) {
    return isNaN(valor.getTime()) ? null : valor;
  }

  const texto = valor.trim();
  if (!texto) return null;

  const m = ISO.exec(texto);
  if (!m) {
    // Formato fora do padrão — devolve ao parser nativo em vez de engolir.
    const nativo = new Date(texto);
    return isNaN(nativo.getTime()) ? null : nativo;
  }

  // Traz fuso declarado: o instante já está definido, não mexer.
  if (m[7]) {
    const comFuso = new Date(texto);
    return isNaN(comFuso.getTime()) ? null : comFuso;
  }

  return new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4] ?? 0),
    Number(m[5] ?? 0),
    Number(m[6] ?? 0)
  );
}

/**
 * `Date` → `yyyy-MM-dd'T'HH:mm:ss` usando os componentes **locais**, sem sufixo
 * de fuso.
 *
 * É o formato que a API emite e que a coluna `timestamp without time zone`
 * guarda. Use ao enviar data **com hora** ao servidor: `toISOString()` produz o
 * mesmo instante com `Z`, e o backend grava o relógio UTC — 3h à frente.
 */
export function dataHoraLocalIso(data: Date | null | undefined): string {
  if (!data || isNaN(data.getTime())) return '';

  const hora = `${data.getHours()}`.padStart(2, '0');
  const min = `${data.getMinutes()}`.padStart(2, '0');
  const seg = `${data.getSeconds()}`.padStart(2, '0');

  return `${dataLocalIso(data)}T${hora}:${min}:${seg}`;
}

/**
 * Texto da API → `yyyy-MM-dd` para alimentar `<input type="date">`.
 *
 * Aceita tanto `2026-08-17` quanto `2026-08-17T00:00:00` e devolve o **mesmo
 * dia** nos dois casos.
 */
export function dataApiParaInput(valor: string | Date | null | undefined): string {
  return dataLocalIso(parseDataApi(valor));
}

/**
 * Formato que o Dapper produz quando a propriedade do response é `string` em vez
 * de `DateTime`: ele converte o `DateTime` com **cultura invariante**, que dá
 * `MM/dd/yyyy HH:mm:ss` — mês primeiro.
 *
 * ⚠ **Esta é a parte frágil de toda a compensação.** O formato não é declarado
 * em lugar nenhum: é consequência de o campo estar tipado como `string` no
 * backend. Se algum dia a cultura do servidor virar pt-BR, `08/09/2026` passa a
 * significar 8 de setembro em vez de 9 de agosto — e vai ser lido errado **sem
 * erro nenhum**. A saída definitiva é tipar esses campos como `DateTime` no
 * response, que os faz sair em ISO.
 */
const INVARIANTE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/;

/**
 * Lê o texto como **relógio UTC** e devolve o instante correspondente, ou `null`
 * quando o texto não é um dos formatos que o servidor manda sem fuso.
 *
 * É o motor do pipe `dataUtc`: só faz sentido em campo cuja coluna é gravada em
 * UTC. Aplicar em coluna local desloca o valor para trás em 3h.
 *
 * Devolve `null` — e portanto não compensa — quando:
 *
 * - a string **declara fuso** (`Z` ou `±hh:mm`): o instante já está definido, e
 *   reinterpretar deslocaria duas vezes;
 * - a string é **data pura** (`2026-08-17`): não há hora para compensar, e
 *   tratá-la como UTC jogaria a data para o dia anterior às 21:00;
 * - o formato é desconhecido: melhor exibir como está do que adivinhar.
 */
export function interpretarComoUtc(valor: string): Date | null {
  const texto = valor.trim();

  const iso = ISO.exec(texto);
  if (iso) {
    // Sem parte de hora, ou com fuso declarado: não compensa.
    if (iso[4] === undefined || iso[7]) return null;

    return montarUtc(+iso[1], +iso[2], +iso[3], +iso[4], +iso[5], +(iso[6] ?? 0));
  }

  const inv = INVARIANTE.exec(texto);
  if (inv) {
    return montarUtc(+inv[3], +inv[1], +inv[2], +inv[4], +inv[5], +(inv[6] ?? 0));
  }

  return null;
}

/**
 * Monta o instante UTC e **confere se os componentes sobreviveram**, devolvendo
 * `null` quando não.
 *
 * Sem essa conferência, `Date.UTC` transborda em silêncio: um `18/08/2026` no
 * formato brasileiro seria lido como mês 18, que rola para junho de 2027 — data
 * plausível na tela e completamente errada. É a diferença entre não compensar
 * (o valor aparece como está) e mostrar uma data inventada.
 */
function montarUtc(
  ano: number, mes: number, dia: number,
  hora: number, minuto: number, segundo: number
): Date | null {
  const d = new Date(Date.UTC(ano, mes - 1, dia, hora, minuto, segundo));

  if (isNaN(d.getTime())) return null;

  const intacto =
    d.getUTCFullYear() === ano &&
    d.getUTCMonth() === mes - 1 &&
    d.getUTCDate() === dia &&
    d.getUTCHours() === hora &&
    d.getUTCMinutes() === minuto &&
    d.getUTCSeconds() === segundo;

  return intacto ? d : null;
}
